import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import {
  api,
  type QuotaChurch,
  type ExclusionCategory,
  type ExclusionExportRow,
  type BulkAmountRow,
} from '../../api/client';
import { fmt } from '@cca/ui';

// Source-data page for contribution amounts (Stipend, Mission, Training, …).
// One YEAR at a time (these change year to year), every church on screen at
// once — rows = churches, columns = categories — so the registrar can key the
// year's figures across all churches and save in one go. Writes go through the
// bulk exclusion-amount API; the policy then decides what % of each to exclude.

const THIS_YEAR = new Date().getFullYear();

/** Comma-separated, no decimals (e.g. 43906 → "43,906"). Blank for 0. */
function fmtAmount(v: number): string {
  return v ? Math.round(v).toLocaleString('en-NZ') : '';
}

export function ContributionsTab() {
  const [churches, setChurches] = useState<QuotaChurch[]>([]);
  const [categories, setCategories] = useState<ExclusionCategory[]>([]);
  const [rows, setRows] = useState<ExclusionExportRow[]>([]);
  const [year, setYear] = useState<number>(THIS_YEAR);
  const [edits, setEdits] = useState<Record<string, number>>({});
  // Cell currently being edited — shown as a plain number (no commas) so typing
  // doesn't fight a reformatting caret; reverts to comma-formatted on blur.
  const [focused, setFocused] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cs, cats, ex] = await Promise.all([
        api.quotaChurches(),
        api.quotaCategories(),
        api.quotaExclusionExport(),
      ]);
      setChurches(cs);
      setCategories(cats.filter((c) => c.active));
      setRows(ex);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  // church_id → year → category_key → amount, from the export.
  const amounts = useMemo(() => {
    const m = new Map<string, Map<number, Record<string, number>>>();
    for (const r of rows) {
      let byYear = m.get(r.church_id);
      if (!byYear) {
        byYear = new Map();
        m.set(r.church_id, byYear);
      }
      byYear.set(r.financial_year, r.category_amounts);
    }
    return m;
  }, [rows]);

  // Years present in the data (+ current year), newest first.
  const years = useMemo(() => {
    const s = new Set<number>([THIS_YEAR]);
    for (const r of rows) s.add(r.financial_year);
    return [...s].sort((a, b) => b - a);
  }, [rows]);

  const existing = (churchId: string, cat: string): number =>
    amounts.get(churchId)?.get(year)?.[cat] ?? 0;
  const key = (churchId: string, cat: string) => `${churchId}|${year}|${cat}`;
  const cellValue = (churchId: string, cat: string): number => {
    const k = key(churchId, cat);
    return k in edits ? (edits[k] ?? 0) : existing(churchId, cat);
  };

  const onEdit = (churchId: string, cat: string, raw: string) => {
    const v = raw.trim() === '' ? 0 : Number(raw.replace(/,/g, ''));
    if (Number.isNaN(v) || v < 0) return;
    setEdits((prev) => ({ ...prev, [key(churchId, cat)]: v }));
  };

  // Only cells whose value actually changed vs the stored figure.
  const changed: BulkAmountRow[] = useMemo(() => {
    const out: BulkAmountRow[] = [];
    for (const [k, v] of Object.entries(edits)) {
      const [church_id, y, category_key] = k.split('|');
      if (!church_id || !y || !category_key) continue;
      const cur = amounts.get(church_id)?.get(Number(y))?.[category_key] ?? 0;
      if (v !== cur) out.push({ church_id, financial_year: Number(y), category_key, amount: v });
    }
    return out;
  }, [edits, amounts]);

  const save = async () => {
    if (changed.length === 0) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.quotaBulkSetExclusionAmounts(changed);
      setNotice(`Saved ${res.written} amount${res.written === 1 ? '' : 's'}.`);
      setEdits({});
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
        Enter each church's contribution amounts for the year (these change year to year). The
        policy on Policy &amp; Run / Scenarios decides what % of each column is excluded.
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <label>
          <span style={{ fontSize: 11, color: 'var(--text2)', display: 'block', marginBottom: 2 }}>
            Year
          </span>
          <select
            className="input"
            value={year}
            style={{ width: 110 }}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <div style={{ flex: 1 }} />
        <button className="btn" disabled={busy || changed.length === 0} onClick={() => void save()}>
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
          &nbsp;Save{changed.length ? ` (${changed.length})` : ''}
        </button>
      </div>

      {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 10 }}>{error}</div>}
      {notice && (
        <div style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 10 }}>{notice}</div>
      )}

      {loading ? (
        <Loader2 className="animate-spin" size={18} />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr
                style={{
                  textAlign: 'right',
                  color: 'var(--text2)',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <th style={{ textAlign: 'left', padding: 6 }}>Church</th>
                {categories.map((cat) => (
                  <th key={cat.category_key} style={{ padding: 6 }}>
                    {cat.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {churches.map((ch) => (
                <tr key={ch.church_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ textAlign: 'left', padding: 6, fontWeight: 600 }}>{ch.name}</td>
                  {categories.map((cat) => {
                    const v = cellValue(ch.church_id, cat.category_key);
                    const k = key(ch.church_id, cat.category_key);
                    const dirty = k in edits;
                    return (
                      <td key={cat.category_key} style={{ padding: 4, textAlign: 'right' }}>
                        <input
                          className="input input-amount"
                          inputMode="numeric"
                          value={focused === k ? (v ? String(v) : '') : fmtAmount(v)}
                          placeholder="0"
                          style={{
                            width: 96,
                            textAlign: 'right',
                            ...(dirty ? { borderColor: 'var(--brand)', fontWeight: 600 } : {}),
                          }}
                          onFocus={() => setFocused(k)}
                          onBlur={() => setFocused((f) => (f === k ? null : f))}
                          onChange={(e) => onEdit(ch.church_id, cat.category_key, e.target.value)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
              {churches.length === 0 && (
                <tr>
                  <td
                    colSpan={categories.length + 1}
                    style={{ padding: 12, color: 'var(--text2)', fontSize: 13 }}
                  >
                    No churches. Sync churches on the Assessment Base tab first.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr
                style={{
                  textAlign: 'right',
                  fontWeight: 700,
                  borderTop: '2px solid var(--border)',
                }}
              >
                <td style={{ textAlign: 'left', padding: 6 }}>Total</td>
                {categories.map((cat) => {
                  const total = churches.reduce(
                    (s, ch) => s + cellValue(ch.church_id, cat.category_key),
                    0,
                  );
                  return (
                    <td key={cat.category_key} style={{ padding: 6 }}>
                      {fmt(total)}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
